import "./polyfills";
import { trpc } from "@/lib/trpc";
import { installStaleSpaRecovery } from "@/lib/lazy-retry";
import { UNAUTHED_ERR_MSG } from "@shared/const";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { LazyMotion, MotionConfig, domAnimation } from "framer-motion";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";

// Install window-level error listeners BEFORE createRoot so post-deploy
// stale-SPA / version-skew errors that fire during initial bundle execution
// (before ErrorBoundary exists) still trigger an auto-reload onto the fresh
// build. See client/src/lib/lazy-retry.ts. Idempotent.
installStaleSpaRecovery();

// Cross-origin "Script error." suppression lives in client/index.html as an
// inline <script> — the very first thing on the page — so it installs before
// Clarity, GA, and Pixel register their own window.onerror / addEventListener
// hooks. Doing it there (rather than here in the React bundle) guarantees
// correct ordering regardless of how analytics scripts load in the future.

// Sentry is lazy-loaded after first paint to keep ~25 KiB gzip out of the
// critical bundle on Ghana 4G. Errors thrown before Sentry initialises are
// captured by a tiny boot-time global handler queue and replayed once Sentry
// is ready (see initDeferredSentry below).
const __sentryQueue: Array<{
  type: "error" | "unhandledrejection";
  payload: unknown;
}> = [];
function initDeferredSentry() {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  // Capture pre-init errors so we don't lose anything that happens during
  // the first ~3s of page load.
  const onError = (e: ErrorEvent) =>
    __sentryQueue.push({ type: "error", payload: e.error ?? e.message });
  const onRejection = (e: PromiseRejectionEvent) =>
    __sentryQueue.push({ type: "unhandledrejection", payload: e.reason });
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);

  const cleanup = () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    __sentryQueue.length = 0;
  };
  const start = () => {
    import("@sentry/react")
      .then(Sentry => {
        Sentry.init({
          dsn: import.meta.env.VITE_SENTRY_DSN,
          environment: import.meta.env.MODE,
          // Replay integration deliberately omitted, Sentry auto-tree-shakes
          // replay/feedback/replay-canvas (~63 KB transferred, ~250 ms eval saved
          // on Slow 4G). Stack traces + breadcrumbs cover most diagnostics; restore
          // replaysOnErrorSampleRate and add the integration to opts when we want
          // visual replays back.
          tracesSampleRate: 0.05,
          // Drop noise from third-party bridges we don't control.
          // - window.webkit.messageHandlers: Facebook / iOS in-app browser bridge
          //  , Meta's own fbevents.js / injected FB code tries to postMessage
          //   to a native handler. We have no control; spams Sentry 100s/day.
          // - window.ReactNativeWebView: similar for Android/RN webviews.
          // - instagram.com/facebook.com/fbcdn: third-party scripts loaded
          //   by embedded browsers.
          ignoreErrors: [
            // iOS Facebook in-app browser, WebKit native bridge
            /window\.webkit\.messageHandlers/,
            /messageHandlers\[.+?\]\.postMessage/,
            /ReactNativeWebView/,
            /Non-Error promise rejection captured/,
            // Android Facebook in-app browser, Java WebView bridge
            // Facebook injects JS that calls native Java methods on GC'd objects.
            // Devices: Itel, Infinix, Tecno, Samsung low-end (Ghana's top phones).
            /Java object is gone/,
            /enableDidUserTypeOnKeyboardLogging/,
            /postMessage.*Java object/,
            // Cross-origin script errors from Facebook's injected scripts
            /^Script error\.?$/,
            // Truncated JSON from network drops on low-RAM/3G devices
            /Unexpected end of input/,
            /Unexpected end of JSON/,
            // Benign browser warning emitted by Radix Select/Popover and other
            // libraries that measure anchors via ResizeObserver. Investigated
            // 2026-06-01 — no custom ResizeObserver usage in this codebase, so
            // these are always library noise. The error does not block the
            // user-facing flow; suppressing it stops Clarity / Sentry from
            // reporting it as a vendor-signup blocker.
            /ResizeObserver loop limit exceeded/,
            /ResizeObserver loop completed with undelivered notifications/,
          ],
          denyUrls: [
            /connect\.facebook\.net/,
            /graph\.facebook\.com/,
            /\.fbcdn\.net/,
            /googletagmanager\.com/,
          ],
          beforeSend(event) {
            const msg =
              event.exception?.values?.[0]?.value || event.message || "";
            // Drop all Facebook in-app browser bridge noise (iOS + Android)
            if (
              msg.includes("messageHandlers") ||
              msg.includes("Java object is gone") ||
              msg.includes("enableDidUserTypeOnKeyboard") ||
              msg.includes("ResizeObserver loop") ||
              msg === "Script error." ||
              msg === "Script error"
            ) {
              return null;
            }
            return event;
          },
        });
        // Replay pre-init errors, but filter out the same Facebook
        // bridge noise we ignore in ignoreErrors. The queue captures
        // errors BEFORE Sentry loads, so ignoreErrors doesn't apply
        // to replayed items unless we check here.
        const isNoise = (s: string) =>
          /Script error|Java object is gone|messageHandlers|enableDidUserType|Unexpected end of/i.test(
            s
          );

        for (const item of __sentryQueue) {
          const p = item.payload;
          const msg = p instanceof Error ? p.message : String(p ?? "");
          if (isNoise(msg)) continue;

          if (p instanceof Error) {
            Sentry.captureException(p);
          } else if (p && typeof p === "object") {
            // Wrap object reasons so Sentry still gets stack + structured data
            Sentry.captureException(
              new Error(
                `Pre-init ${item.type}: ${JSON.stringify(p).slice(0, 500)}`
              )
            );
          } else {
            Sentry.captureMessage(`Pre-init ${item.type}: ${String(p)}`);
          }
        }
        cleanup();
      })
      .catch(() => {
        // Sentry failed to load, non-fatal. Drop the queue + listeners so
        // we don't grow unboundedly on every subsequent error.
        cleanup();
      });
  };

  // requestIdleCallback fires after the browser is idle (post-LCP). Falls
  // back to setTimeout for Safari/older browsers.
  if ("requestIdleCallback" in window) {
    (
      window as unknown as {
        requestIdleCallback: (
          cb: () => void,
          opts?: { timeout: number }
        ) => void;
      }
    ).requestIdleCallback(start, { timeout: 4000 });
  } else {
    setTimeout(start, 3000);
  }
}
initDeferredSentry();

/** Read the CSRF token from the double-submit cookie */
function getCsrfToken(): string {
  const match = document.cookie
    .split(";")
    .find(c => c.trim().startsWith("_csrf="));
  return match
    ? decodeURIComponent(match.split("=").slice(1).join("=").trim())
    : "";
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 5 min: Ghana 3G/4G is round-trip-expensive. refetchOnWindowFocus and
      // refetchOnReconnect are already off, and mutations invalidate the
      // queries that need freshness, so a longer staleTime cuts data usage
      // for users browsing product/search lists without compromising writes.
      staleTime: 5 * 60_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;
  if (window.location.pathname === "/login") return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      headers() {
        // Forward visitorId so server-side events can be sessionized.
        let vid = "";
        try {
          vid = localStorage.getItem("voom_vid") || "";
        } catch {
          /* */
        }
        return {
          "x-trpc-source": "client",
          "x-csrf-token": getCsrfToken(),
          ...(vid ? { "x-visitor-id": vid } : {}),
        };
      },
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

// Register service worker for PWA on every visit (after `load` so it doesn't
// compete with critical resources). First-time Ghana users benefit from the
// cache warming during their first session: subsequent in-session navigations
// hit the SW cache instead of the network.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

// Framer Motion is wired here once at the root:
// - MotionConfig reducedMotion="user" honors the OS-level "Reduce Motion"
//   preference globally; no per-component opt-in needed.
// - LazyMotion + domAnimation tree-shakes Framer Motion down to ~12kb
//   (vs ~30kb full bundle) — mandatory for Ghana 3G/4G targets.
// - strict makes any use of full `motion.*` throw at runtime so Phase 2
//   agents catch the bundle-size mistake immediately. Use `m.*` (e.g.
//   `<m.div />`) in all downstream code. See ~/.claude/skills/framer-motion.
createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <MotionConfig reducedMotion="user">
        <LazyMotion features={domAnimation} strict>
          <App />
        </LazyMotion>
      </MotionConfig>
    </QueryClientProvider>
  </trpc.Provider>
);
